import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, Users, Gamepad2, MessageSquare, Crown, Trophy, Star } from "lucide-react";
import { FeaturedItem } from "@/services/featuredItemsService";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 }
  }
};

interface Community {
  id: string;
  name: string;
  slug: string;
  description: string;
  cover_image_url?: string;
  member_count: number;
  games?: {
    id: string;
    name: string;
    icon?: string;
  };
  mentor?: {
    profile: {
      nickname: string;
      image_url?: string;
    };
  };
}

interface CommunitiesSectionProps {
  communities: Community[] | undefined;
  isLoading?: boolean;
  error?: Error | null;
  featuredItems?: FeaturedItem[];
}

export const CommunitiesSection: React.FC<CommunitiesSectionProps> = ({ 
  communities, 
  isLoading, 
  error,
  featuredItems
}) => {
  const navigate = useNavigate();

  // Função para obter ícone da posição
  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Crown className="h-4 w-4 text-yellow-400" />;
      case 2:
        return <Trophy className="h-4 w-4 text-gray-400" />;
      case 3:
        return <Star className="h-4 w-4 text-amber-600" />;
      default:
        return null;
    }
  };

  // Função para obter classe da borda do destaque
  const getFeaturedBorderClass = (position: number) => {
    switch (position) {
      case 1:
        return "border-yellow-400 shadow-yellow-400/20";
      case 2:
        return "border-gray-400 shadow-gray-400/20";
      case 3:
        return "border-amber-600 shadow-amber-600/20";
      default:
        return "border-[#223342]";
    }
  };

  return (
    <section id="communities-section" className="py-16 md:py-24 px-4 relative">
      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#0f141c] to-transparent z-10"></div>
      
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-12">
          <Users className="h-6 w-6 text-[#00B6F0]" />
          <h2 className="text-3xl md:text-4xl font-bold text-white">Comunidades em Destaque</h2>
        </div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center"
        >
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="w-full max-w-sm h-80 bg-[#181D24] rounded-xl animate-pulse"></div>
            ))
          ) : error ? (
            <div className="col-span-full text-center py-12">
              <p className="text-red-400 mb-4">Erro ao carregar comunidades</p>
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline" 
                className="border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0]/10"
              >
                Tentar Novamente
              </Button>
            </div>
          ) : communities && communities.length > 0 ? (
            communities.map(community => {
              const featuredItem = featuredItems?.find(item => item.item_id === community.id);
              const position = featuredItem?.position || 0;
              const isFeatured = !!featuredItem;
              
              return (
                <motion.div 
                  key={community.id} 
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  className="w-full max-w-sm relative"
                >
                  {/* Badge de posição para comunidades em destaque */}
                  {isFeatured && (
                    <div className="absolute -top-2 -right-2 z-30 bg-gradient-to-r from-[#00B6F0] to-[#0099CC] rounded-full p-2 shadow-lg">
                      {getPositionIcon(position)}
                    </div>
                  )}
                  
                  <div className={`bg-gradient-to-b from-[#1E2530] to-[#0F141A] rounded-xl overflow-hidden shadow-lg border-2 ${isFeatured ? getFeaturedBorderClass(position) : 'border-[#223342]'} hover:border-[#00B6F0]/50 transition-all`}>
                    {/* Cover Image */}
                    <div className="relative h-40">
                      {community.cover_image_url ? (
                        <img
                          src={community.cover_image_url}
                          alt={`${community.name} cover`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = "/placeholder.svg";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-r from-blue-600 to-purple-600" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1E2530] z-10"></div>

                      {/* Game Badge */}
                      {community.games && (
                        <div className="absolute top-3 right-3 z-20">
                          <div className="bg-[#282F3C]/80 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-2">
                            {community.games.icon && (
                              <img 
                                src={community.games.icon} 
                                alt={community.games.name}
                                className="w-4 h-4 rounded"
                              />
                            )}
                            <span className="text-white text-sm font-medium">{community.games.name}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="px-5 py-5">
                      {/* Community Name */}
                      <div className="text-center mb-3">
                        <h3 className="text-xl font-bold text-white mb-2">{community.name}</h3>
                        
                        {/* Mentor Info */}
                        {community.mentor && (
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              {community.mentor.profile.image_url ? (
                                <img 
                                  src={community.mentor.profile.image_url} 
                                  alt={community.mentor.profile.nickname}
                                  className="w-5 h-5 rounded-full object-cover border border-gray-600"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-[#00B6F0] flex items-center justify-center text-xs text-white font-bold">
                                  {community.mentor.profile.nickname.substring(0, 1).toUpperCase()}
                                </div>
                              )}
                              <span className="text-gray-300 text-sm">
                                Por <span className="text-[#00B6F0] font-medium">{community.mentor.profile.nickname}</span>
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {community.description && (
                          <p className="text-gray-400 text-sm">
                            {community.description.length > 100 
                              ? `${community.description.substring(0, 100)}...` 
                              : community.description
                            }
                          </p>
                        )}
                      </div>
                      
                      {/* Stats */}
                      <div className="flex justify-center items-center gap-4 mb-4">
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-[#00B6F0]" />
                          <span className="text-gray-300 text-sm">{community.member_count} membros</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageSquare className="h-4 w-4 text-green-400" />
                          <span className="text-gray-300 text-sm">Ativa</span>
                        </div>
                      </div>
                      
                      {/* Join Community Button */}
                      <Button 
                        onClick={() => navigate(`/comunidades/${community.slug}`)}
                        className="w-full bg-[#00B6F0] hover:bg-[#0099CC] text-black font-medium rounded-md"
                      >
                        Ver Comunidade
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-400">Nenhuma comunidade encontrada</p>
            </div>
          )}
        </motion.div>

        <div className="mt-12 text-center">
          <Button 
            onClick={() => navigate("/comunidades")} 
            variant="outline" 
            size="lg" 
            className="border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0]/10"
          >
            Ver Todas as Comunidades
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}; 